/**
 * The clinic's brand kit and the static generator built on it (Aziz 2026-10-21).
 *
 * One kit per clinic: uploaded fonts (three Arabic, three English), colours, logos, voice,
 * compliance, people, services, and templates. A template is an uploaded background plus
 * boxes placed exactly (headline, photo, logo, call to action...), each tied to a kit font
 * slot and a kit colour token, never a free font or colour. Every static is drawn on a
 * canvas by renderDesign(), the same code for the editor preview and the exported PNG, so
 * Arabic is shaped by the browser with the clinic's own fonts and never by an image model.
 */
import { api } from './api';

export const FONT_SLOTS = ['ar_primary', 'ar_secondary', 'ar_tertiary', 'en_primary', 'en_secondary', 'en_tertiary'] as const;
export type FontSlot = (typeof FONT_SLOTS)[number];
export const COLOUR_TOKENS = ['primary', 'secondary', 'accent', 'background', 'text', 'white', 'black'] as const;
export type ColourToken = (typeof COLOUR_TOKENS)[number];
export const ROLES = ['headline', 'subheadline', 'body', 'caption', 'cta', 'name', 'title', 'specialties',
  'contact', 'signature', 'disclaimer', 'photo', 'image', 'logo', 'shape', 'custom',
  // before and after (Aziz 2026-10-22): the two patient photos and their "Before" / "After" tags
  'before_photo', 'after_photo', 'label',
  // decorative icons and symbols (designer, 2026-10-24): a faded question mark behind the text
  'icon'] as const;
export type Role = (typeof ROLES)[number];
/** What the kit's asset rules cover: every element role, and the template's background layer (owner 2026-10-24). */
export type RuleKey = Role | 'background';
/** The background is always the bottom layer; the editor's Layers list shows it as this row. */
export const BG_LAYER = '__bg';
/** Photos and colour blocks go in just above the background, text and logos on top, so text stays in front. */
export const BACK_ROLES: Role[] = ['photo', 'image', 'before_photo', 'after_photo', 'shape', 'icon'];

// ------------------------------------------------------------------ icons and symbols
/** Line icons on a 24 x 24 grid, drawn in a kit colour (stroke), optionally filled with a second token.
 *  The last ten are the Instagram highlight cover icons. Glyphs are set in the brand font instead. */
export const ICONS: { key: string; label: string; paths?: string[]; glyph?: { en: string; ar: string } }[] = [
  { key: 'question', label: 'Question mark', glyph: { en: '?', ar: '؟' } },
  { key: 'quote', label: 'Quote marks', glyph: { en: '\u201C', ar: '\u201D' } },
  { key: 'plus', label: 'Plus', paths: ['M12 5v14', 'M5 12h14'] },
  { key: 'check', label: 'Check', paths: ['M20 6 9 17l-5-5'] },
  { key: 'arrow', label: 'Arrow', paths: ['M5 12h14', 'M13 5l7 7-7 7'] },
  { key: 'sparkle', label: 'Sparkle', paths: ['M12 3l1.9 5.8L20 11l-6.1 2.2L12 19l-1.9-5.8L4 11l6.1-2.2z'] },
  { key: 'heart', label: 'Heart', paths: ['M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7z'] },
  { key: 'star', label: 'Star', paths: ['M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z'] },
  { key: 'reviews', label: 'Highlight: Reviews', paths: ['M7.9 20A9 9 0 1 0 4 16.1L2 22z', 'M12 15.5l-3-3a1.9 1.9 0 0 1 3-2.4 1.9 1.9 0 0 1 3 2.4z'] },
  { key: 'skin', label: 'Highlight: Skin', paths: ['M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 10.1 5 12 5 15a7 7 0 0 0 7 7z'] },
  { key: 'dental', label: 'Highlight: Dental', paths: ['M7 3c-2.5 0-4 2-4 4.5 0 3 1.5 4.5 2 7 .5 2.5 1 6.5 3 6.5s2-4 4-4 2 4 4 4 2.5-4 3-6.5c.5-2.5 2-4 2-7C21 5 19.5 3 17 3c-2 0-3 1-5 1S9 3 7 3z'] },
  { key: 'laser', label: 'Highlight: Laser', paths: ['M13 2 3 14h9l-1 8 10-12h-9l1-8z'] },
  { key: 'team', label: 'Highlight: Team', paths: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'] },
  { key: 'book', label: 'Highlight: Book', paths: ['M8 2v4', 'M16 2v4', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'M3 10h18'] },
  { key: 'faq', label: 'Highlight: FAQ', paths: ['M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z', 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3', 'M12 17h.01'] },
  { key: 'visit', label: 'Highlight: Visit', paths: ['M20 10c0 4.99-5.54 10.19-7.4 11.8a1 1 0 0 1-1.2 0C9.54 20.19 4 14.99 4 10a8 8 0 0 1 16 0z', 'M15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0z'] },
  { key: 'clinic', label: 'Highlight: Clinic', paths: ['M4 21V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16', 'M2 21h20', 'M12 7v6', 'M9 10h6', 'M10 21v-4h4v4'] },
  { key: 'about', label: 'Highlight: About', paths: ['M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z', 'M12 16v-4', 'M12 8h.01'] },
  { key: 'text', label: 'Any symbol (type it)' },
];
export const FADE_SIDES = ['bottom', 'top', 'left', 'right', 'bottom_right', 'bottom_left', 'top_right', 'top_left'] as const;
export type FadeSide = (typeof FADE_SIDES)[number];
export const IMAGE_ROLES: Role[] = ['photo', 'image', 'logo', 'before_photo', 'after_photo'];
/** The patient photos of a before and after; a design holding either is one. */
export const BEFORE_AFTER_ROLES: Role[] = ['before_photo', 'after_photo'];
export const isBeforeAfter = (t?: Template) => !!t?.elements.some((e) => BEFORE_AFTER_ROLES.includes(e.role));
export const BEFORE_AFTER_OFF = 'Before and after is switched off for this clinic. A manager can switch it on in Brand kit > Compliance.';
export const FONT_LABEL: Record<FontSlot, string> = {
  ar_primary: 'Arabic primary', ar_secondary: 'Arabic secondary', ar_tertiary: 'Arabic tertiary',
  en_primary: 'English primary', en_secondary: 'English secondary', en_tertiary: 'English tertiary',
};
// Social sizes as of Oct 2026. Labels name the platforms each size serves; keep 'feed' and 'story' first
// (FORMATS[0] / FORMATS[1] are the defaults for new templates and the recipe gallery).
export const FORMATS = [
  { key: 'feed', label: 'Feed 4:5 (1080 x 1350)', w: 1080, h: 1350 },
  { key: 'story', label: 'Story / Reel cover 9:16 (1080 x 1920)', w: 1080, h: 1920 },
  { key: 'square', label: 'Square 1:1 (1080 x 1080) - feed and ads', w: 1080, h: 1080 },
  { key: 'portrait34', label: 'Portrait 3:4 (1080 x 1440) - Instagram post and grid', w: 1080, h: 1440 },
  { key: 'landscape43', label: 'Landscape 4:3 (1440 x 1080) - Google profile', w: 1440, h: 1080 },
  { key: 'link', label: 'Link 1.91:1 (1200 x 628) - link previews', w: 1200, h: 628 },
  { key: 'yt_thumb', label: 'YouTube thumbnail 16:9 (1280 x 720)', w: 1280, h: 720 },
  { key: 'landscape', label: 'Landscape 16:9 (1920 x 1080) - YouTube, X, website', w: 1920, h: 1080 },
  { key: 'pinterest', label: 'Pinterest 2:3 (1000 x 1500)', w: 1000, h: 1500 },
  { key: 'profile', label: 'Profile picture 1:1 (400 x 400)', w: 400, h: 400 },
  { key: 'fb_cover', label: 'Facebook cover (1640 x 624)', w: 1640, h: 624 },
  { key: 'yt_banner', label: 'YouTube banner (2560 x 1440)', w: 2560, h: 1440 },
  { key: 'x_header', label: 'X header (1500 x 500)', w: 1500, h: 500 },
  { key: 'li_cover', label: 'LinkedIn cover (1584 x 396)', w: 1584, h: 396 },
  { key: 'a4', label: 'A4 print (1240 x 1754)', w: 1240, h: 1754 },
];

/** Brand asset: only managers change it. Flexible: the team edits it (Aziz 2026-10-22). */
export type AssetRule = 'brand' | 'flexible';
export const DEFAULT_BRAND_ROLES: Role[] = ['logo', 'shape', 'title', 'disclaimer', 'signature'];
export function roleRule(kit: BrandKit, role: RuleKey): AssetRule {
  return kit.asset_rules?.[role] ?? (DEFAULT_BRAND_ROLES.includes(role as Role) ? 'brand' : 'flexible');
}
/** The element's own choice wins, else its role's (same rule as the server). */
export function isBrand(kit: BrandKit, e: Element): boolean {
  const own = e.asset || (e.locked ? 'brand' : '');
  return (own || roleRule(kit, e.role)) === 'brand';
}
export interface FontFile { path: string; family: string; google: string }
export interface Element {
  id: string; role: Role; x: number; y: number; w: number; h: number;
  font: FontSlot; size: number; weight: number; colour: ColourToken; fill: ColourToken | '';
  align: 'start' | 'center' | 'end'; valign: 'top' | 'middle' | 'bottom'; max_lines: number;
  radius: number; fit: 'cover' | 'contain'; opacity: number;
  default_en: string; default_ar: string; locked: boolean;
  /** '' = follow the kit's rule for this role; locked is the pre-2026-10-22 flag, read as brand */
  asset?: '' | AssetRule;
  line_height: number; tracking: number; upper: boolean; shadow: boolean;
  gradient_to: ColourToken | ''; gradient_angle: number; stroke: ColourToken | ''; stroke_w: number;
  tint: ColourToken | ''; tint_opacity: number; hidden: boolean; pinned: boolean;
  gradient_type?: 'linear' | 'radial'; highlight?: ColourToken | ''; emph_colour?: ColourToken | '';
  img_blur?: number; img_bw?: boolean; img_brightness?: number;
  /** scrim_side 'grid' = the Instagram grid haze (Aziz 2026-10-26): a light veil, soft edges, strongest behind the bottom words */
  scrim?: ColourToken | ''; scrim_strength?: number; scrim_side?: 'bottom' | 'top' | 'left' | 'right' | 'grid'; scrim_size?: number;
  /** the photo may zoom out below 'fill', the frame's background showing around it (white-background portraits) */
  shrink?: boolean;
  /** where every new photo in this frame starts (and where Reset returns): the template's own placement,
   *  so nobody has to judge the grid crop by eye (Aziz 2026-10-26) */
  default_crop?: Crop;
  rotation?: number; emph_italic?: boolean;
  /** stays where it is in the Arabic version (before left, after right in both languages) */
  keep_side?: boolean;
  /** fade out gradually (designer, 2026-10-24): '' none | linear | radial; side = where it fades to;
   *  start / end = how visible it is at each end of the fade (0 to 1) */
  fade?: '' | 'linear' | 'radial'; fade_side?: FadeSide; fade_start?: number; fade_end?: number;
  /** icon elements: an ICONS key */
  icon?: string;
  /** the layer's own name in the Layers panel ('' = its role) */
  name?: string;
  /** how the layer mixes with the layers beneath it ('' = normal) */
  blend?: Blend;
}
/** Layer blend modes: canvas composite operations, the same in the editor and the export. */
export const BLENDS = [
  { value: '', label: 'Normal', tip: 'Covers what is beneath' },
  { value: 'multiply', label: 'Multiply', tip: 'Darkens: white disappears, colours deepen what is beneath' },
  { value: 'screen', label: 'Screen', tip: 'Lightens: black disappears' },
  { value: 'overlay', label: 'Overlay', tip: 'More contrast: darks darker, lights lighter' },
  { value: 'soft-light', label: 'Soft light', tip: 'A gentle overlay' },
  { value: 'darken', label: 'Darken', tip: 'Keeps the darker colour of the two' },
  { value: 'lighten', label: 'Lighten', tip: 'Keeps the lighter colour of the two' },
  { value: 'color', label: 'Colour', tip: 'Tints what is beneath with this layer’s colour' },
] as const;
export type Blend = (typeof BLENDS)[number]['value'];
export interface Template {
  id: string; name: string; purpose: string; lang: 'ar' | 'en' | 'both'; w: number; h: number;
  background: string; background_colour: ColourToken | ''; mirror_ar: boolean; elements: Element[];
  bg_gradient_to?: ColourToken | ''; bg_gradient_type?: 'linear' | 'radial'; bg_gradient_angle?: number;
  bg_grain?: number; bg_blur?: number; bg_tint?: ColourToken | ''; bg_tint_opacity?: number; recipe?: string;
  /** the background layer (owner 2026-10-24): 1 = as uploaded, 0 = faded away into the colour under it */
  bg_opacity?: number;
  /** zoom (1 = cover) and offset (-1..1 of the overflow) of the background photo, like a photo frame */
  bg_scale?: number; bg_offset_x?: number; bg_offset_y?: number;
  /** fade the background photo gradually into the colour under it */
  bg_fade?: '' | 'linear' | 'radial'; bg_fade_side?: FadeSide; bg_fade_start?: number; bg_fade_end?: number;
}
export interface Person {
  name_en: string; name_ar: string; title_en: string; title_ar: string; licence: string;
  specialties_en: string; specialties_ar: string; languages: string; days_en: string; days_ar: string;
  bio_en: string; bio_ar: string; photo: string; ads_consent: boolean; shown: boolean;
}
export interface Service {
  id: string; name_en: string; name_ar: string; category_en: string; category_ar: string;
  line_en: string; line_ar: string; advertisable: boolean; sensitive: boolean;
}
export interface BrandKit {
  identity: {
    brand_en: string; brand_ar: string; tagline_en: string; tagline_ar: string; website: string; phone: string;
    whatsapp: string; instagram: string; address_en: string; address_ar: string; hours_en: string; hours_ar: string;
  };
  logos: { main: string; white: string; icon: string };
  type: { fonts: Partial<Record<FontSlot, FontFile>>; heading_weight: number; sizes: Record<'h1' | 'h2' | 'body' | 'caption' | 'button', number> };
  colours: { primary: string; secondary: string; accent: string; background: string; text: string; never: string[] };
  voice: { tone: string; dialect: string; signature_en: string; signature_ar: string; banned: string[] };
  compliance: { regulator: string; ad_permit: string; no_prices: boolean; disclaimer_en: string; disclaimer_ar: string; rules: string[];
    /** patient before and after photos; off until a manager switches it on (Aziz 2026-10-22) */
    before_after_allowed?: boolean };
  people: Record<string, Person>;
  services: Service[];
  templates: Template[];
  references: { path: string; note: string }[];
  /** role -> brand | flexible; the server fills the defaults for an older kit */
  asset_rules?: Partial<Record<RuleKey, AssetRule>>;
}
export interface KitPerson { talent_id: string; name: string; tag: string | null; info: Person }
export interface BrandKitOut {
  kit: BrandKit; people: KitPerson[]; client_name: string; complete_pct: number;
  missing: { area: string; item: string; needed_for: string }[];
}

// ------------------------------------------------------------------ API
export async function fetchBrandKit(clientId: string): Promise<BrandKitOut> {
  return (await api.get(`/clients/${clientId}/brand-kit`)).data;
}
export async function saveBrandKit(clientId: string, kit: BrandKit): Promise<BrandKitOut> {
  return (await api.put(`/clients/${clientId}/brand-kit`, kit)).data;
}
/** The base templates: Brand details only (owner 2026-10-24); the identity sections stay as stored. */
export async function saveTemplates(clientId: string, templates: Template[]): Promise<BrandKitOut> {
  return (await api.put(`/clients/${clientId}/brand-kit/templates`, templates)).data;
}

// ------------------------------------------------------------------ personal templates (owner 2026-10-24)
/** A staff member's own design on one clinic: private until shared with the team, people or the clinic. */
export interface UserTemplate {
  id: string; client_id: string; owner_user_id: string; owner_name: string; name: string; template: Template;
  based_on: string | null; shared_team: boolean; shared_user_ids: string[]; shared_client: boolean;
  created_at: string; updated_at: string; mine: boolean; can_edit: boolean;
}
export interface VisibleTemplates {
  base: Template[]; mine: UserTemplate[]; shared: UserTemplate[];
  /** owner and manager: everyone else's on this clinic, shared or not */
  team: UserTemplate[];
  can_base: boolean; sees_all: boolean; people: { id: string; name: string }[];
}
export interface ShareSettings { shared_team: boolean; shared_user_ids: string[]; shared_client: boolean }
export async function fetchVisibleTemplates(clientId: string): Promise<VisibleTemplates> {
  return (await api.get(`/clients/${clientId}/brand-kit/templates/visible`)).data;
}
export async function createMyTemplate(clientId: string, body: { name: string; template: Template; based_on?: string | null; copy_of?: string | null }): Promise<UserTemplate> {
  return (await api.post(`/clients/${clientId}/brand-kit/my-templates`, body)).data;
}
export async function saveMyTemplate(clientId: string, id: string, body: { name?: string; template?: Template }): Promise<UserTemplate> {
  return (await api.put(`/clients/${clientId}/brand-kit/my-templates/${id}`, body)).data;
}
export async function deleteMyTemplate(clientId: string, id: string): Promise<void> {
  await api.delete(`/clients/${clientId}/brand-kit/my-templates/${id}`);
}
export async function shareMyTemplate(clientId: string, id: string, body: ShareSettings): Promise<UserTemplate> {
  return (await api.put(`/clients/${clientId}/brand-kit/my-templates/${id}/share`, body)).data;
}
export async function promoteMyTemplate(clientId: string, id: string): Promise<BrandKitOut> {
  return (await api.post(`/clients/${clientId}/brand-kit/my-templates/${id}/promote`)).data;
}

/** One pickable design, in the Templates tab and in Generate. */
export interface TemplateEntry {
  key: string; kind: 'base' | 'mine' | 'shared' | 'team'; group: string; template: Template; row?: UserTemplate;
}
export const BASE_GROUP = 'Base templates';
export const MINE_GROUP = 'My templates';
export const SHARED_GROUP = 'Shared with me';
/** Base first, then mine, then shared with me, then (owner and manager) each person's own. */
export function templateEntries(v: VisibleTemplates): TemplateEntry[] {
  const out: TemplateEntry[] = v.base.map((t) => ({ key: `base:${t.id}`, kind: 'base', group: BASE_GROUP, template: t }));
  for (const r of v.mine) out.push({ key: `ut:${r.id}`, kind: 'mine', group: MINE_GROUP, template: r.template, row: r });
  for (const r of v.shared) out.push({ key: `ut:${r.id}`, kind: 'shared', group: SHARED_GROUP, template: r.template, row: r });
  const team = [...v.team].sort((a, b) => a.owner_name.localeCompare(b.owner_name));
  for (const r of team) out.push({ key: `ut:${r.id}`, kind: 'team', group: `${r.owner_name}'s templates`, template: r.template, row: r });
  return out;
}

/** The clinic's portal: designs shared with it, with just enough of the kit to draw them. */
export interface PortalDesigns { client_name: string; kit: BrandKit; templates: { id: string; name: string; updated_at: string; template: Template }[] }
export async function fetchPortalDesigns(): Promise<PortalDesigns> {
  return (await api.get('/portal/designs')).data;
}
/** The client id the portal passes to the canvas: its files come through /portal/designs/file. */
export const PORTAL_CLINIC = 'portal';
/** Upload one kit file straight to storage; returns its path for the kit. */
export async function uploadKitFile(clientId: string, slot: string, file: File): Promise<string> {
  const r = (await api.post(`/clients/${clientId}/brand-kit/upload-url`, {
    slot, content_type: file.type, filename: file.name,
  })).data as { upload_url: string; path: string; content_type: string };
  const put = await fetch(r.upload_url, { method: 'PUT', headers: { 'Content-Type': r.content_type }, body: file });
  if (!put.ok) throw new Error(`Storage refused the file (${put.status})`);
  return r.path;
}

const blobCache = new Map<string, Promise<Blob>>();
/** A kit file's bytes, through the API (same origin, so a canvas can use it). */
export function kitBlob(clientId: string, path: string): Promise<Blob> {
  const key = `${clientId}|${path}`;
  let p = blobCache.get(key);
  if (!p) {
    const url = clientId === PORTAL_CLINIC ? '/portal/designs/file' : `/clients/${clientId}/brand-kit/file`;
    p = api.get(url, { params: { path }, responseType: 'blob' }).then((r) => r.data as Blob);
    p.catch(() => blobCache.delete(key));
    blobCache.set(key, p);
  }
  return p;
}

const imageCache = new Map<string, Promise<HTMLImageElement>>();
export function loadImageSrc(src: string): Promise<HTMLImageElement> {
  let p = imageCache.get(src);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image failed to load'));
      img.src = src;
    });
    imageCache.set(src, p);
  }
  return p;
}
export async function kitImage(clientId: string, path: string): Promise<HTMLImageElement> {
  const blob = await kitBlob(clientId, path);
  return loadImageSrc(URL.createObjectURL(blob));
}

// ------------------------------------------------------------------ fonts
/** CSS family name the canvas uses for a slot. Scoped by path so a re-upload is a new face. */
export function familyFor(kit: BrandKit, slot: FontSlot): string {
  const f = kit.type.fonts[slot];
  if (f?.path) return `kit-${slot}-${f.path.slice(-12).replace(/[^a-z0-9]/gi, '')}`;
  if (f?.google) return f.google;
  return slot.startsWith('ar') ? 'Segoe UI' : 'Inter';
}
const loadedFaces = new Set<string>();
const loadedGoogle = new Set<string>();
/** Make every kit font available to the canvas before drawing. */
export async function loadKitFonts(clientId: string, kit: BrandKit): Promise<void> {
  const jobs: Promise<unknown>[] = [];
  for (const slot of FONT_SLOTS) {
    const f = kit.type.fonts[slot];
    if (!f) continue;
    if (f.path) {
      const fam = familyFor(kit, slot);
      if (loadedFaces.has(fam)) continue;
      jobs.push(kitBlob(clientId, f.path).then(async (b) => {
        // one file covers every weight we ask for, so the browser never fakes a bold over a bold face
        const face = new FontFace(fam, await b.arrayBuffer(), { weight: '100 900' });
        await face.load();
        document.fonts.add(face);
        loadedFaces.add(fam);
      }));
    } else if (f.google && !loadedGoogle.has(f.google)) {
      loadedGoogle.add(f.google);
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.google)}:wght@300;400;500;600;700;800&display=block`;
      document.head.appendChild(link);
      jobs.push(new Promise((r) => { link.onload = r; link.onerror = r; }).then(() =>
        // the sample text decides which subsets download: without Arabic in it, Arabic would fall back
        Promise.all([300, 400, 500, 600, 700, 800].map((w) => document.fonts.load(`${w} 40px "${f.google}"`, 'Aa وجهك ١٢٣').catch(() => null)))));
    }
  }
  await Promise.allSettled(jobs);
}

/** Brand fonts only, never a silent fallback (owner 2026-10-23). For the text a design shows in
 *  `lang`: the slots with no kit font, and the slots whose kit font did not load into the page. */
export async function fontProblems(clientId: string, kit: BrandKit, t: Template, lang: Lang): Promise<{ missing: FontSlot[]; failed: FontSlot[] }> {
  const need = new Set<FontSlot>();
  for (const e of t.elements) if (!e.hidden && e.role !== 'shape' && !IMAGE_ROLES.includes(e.role) && !(e.role === 'icon' && !iconGlyph(e, lang))) need.add(slotFor(e, lang));
  const missing: FontSlot[] = [], failed: FontSlot[] = [];
  if (!need.size) return { missing, failed };
  await loadKitFonts(clientId, kit);
  const loaded = new Set(Array.from(document.fonts as unknown as Iterable<FontFace>)
    .filter((f) => f.status === 'loaded').map((f) => f.family.replace(/["']/g, '')));
  for (const s of need) {
    const f = kit.type.fonts[s];
    if (!f || !(f.path || f.google)) missing.push(s);
    else if (!loaded.has(familyFor(kit, s))) failed.push(s);
  }
  return { missing, failed };
}

// ------------------------------------------------------------------ colours
export function colourOf(kit: BrandKit, token: ColourToken | ''): string {
  if (!token) return 'transparent';
  if (token === 'white') return '#ffffff';
  if (token === 'black') return '#000000';
  const v = kit.colours[token];
  return v || { primary: '#1f2937', secondary: '#6b7280', accent: '#b08d57', background: '#ffffff', text: '#111111' }[token];
}

// ------------------------------------------------------------------ content
export type Lang = 'ar' | 'en';
export interface Fill { text?: string; image?: string /* kit path or blob: URL */ }
export interface Context { lang: Lang; person?: KitPerson; service?: Service }

/** What a box says before anyone types: from the kit, the chosen person and service. */
export function autoText(kit: BrandKit, el: Element, ctx: Context): string {
  const ar = ctx.lang === 'ar';
  const p = ctx.person?.info;
  const s = ctx.service;
  const pick = (en?: string, a?: string) => ((ar ? a : en) || '').trim();
  const own = pick(el.default_en, el.default_ar);
  switch (el.role) {
    case 'name': return pick(p?.name_en || ctx.person?.name, p?.name_ar) || own;
    case 'title': {
      const reg = kit.compliance.regulator || '';
      const lic = p?.licence ? (reg && !p.licence.toUpperCase().startsWith(reg.toUpperCase()) ? `${reg} ${p.licence}` : p.licence) : '';
      return [pick(p?.title_en, p?.title_ar), lic].filter(Boolean).join(' | ') || own;
    }
    case 'specialties': return pick(p?.specialties_en, p?.specialties_ar) || own;
    case 'headline': return own || pick(s?.name_en, s?.name_ar);
    case 'subheadline': return own || pick(s?.line_en, s?.line_ar) || pick(kit.identity.tagline_en, kit.identity.tagline_ar);
    case 'body': return own || pick(p?.bio_en, p?.bio_ar) || pick(s?.line_en, s?.line_ar);
    case 'contact': return own || [kit.identity.whatsapp || kit.identity.phone, kit.identity.website].filter(Boolean).join('   ');
    case 'signature': return own || pick(kit.voice.signature_en, kit.voice.signature_ar);
    case 'disclaimer': return own || pick(kit.compliance.disclaimer_en, kit.compliance.disclaimer_ar);
    default: return own;
  }
}
/** The image a box shows before anyone picks one. */
export function autoImage(kit: BrandKit, el: Element, ctx: Context): string {
  if (el.role === 'logo') return el.colour === 'white' ? (kit.logos.white || kit.logos.main) : (kit.logos.main || kit.logos.white);
  if (el.role === 'photo') return ctx.person?.info.photo || '';
  return '';
}
/** The template as drawn in `lang`: Arabic flips every box left to right when mirror_ar is on. */
export function placed(t: Template, lang: Lang): Template {
  if (lang !== 'ar' || t.mirror_ar === false) return t;
  return { ...t, elements: t.elements.map((e) => (e.keep_side ? e : { ...e, x: t.w - e.x - e.w, ...(e.fade ? { fade_side: mirrorSide(e.fade_side) } : {}) })) };
}
const mirrorSide = (s?: FadeSide): FadeSide => (s ? (s.includes('left') ? s.replace('left', 'right') : s.replace('right', 'left')) as FadeSide : 'bottom');
/** Arabic text is set in the Arabic twin of the box's slot (en_primary -> ar_primary). */
export function slotFor(el: Element, lang: Lang): FontSlot {
  const tier = el.font.split('_')[1];
  return `${lang}_${tier}` as FontSlot;
}

// ------------------------------------------------------------------ compliance
const PRICE = /(\d[\d,.]*\s?(aed|dhs?|درهم|د\.إ))|((aed|dhs?)\s?\d)|\d+\s?%|\b(offer|discount|sale|promo|deal|free)\b|خصم|عرض خاص|تخفيض|مجان/i;
export function complianceWarnings(kit: BrandKit, texts: string[], opts?: { template?: Template; texts?: Record<string, string> }): string[] {
  const out: string[] = [];
  const all = texts.join('\n');
  for (const w of kit.voice.banned) {
    const t = w.trim();
    if (!t) continue;
    // English words match whole words ("only" must not flag "commonly"); Arabic matches inside words,
    // because Arabic attaches prefixes and suffixes (الأفضل, وبالأفضل)
    const latin = /^[ -~]+$/.test(t);
    const esc = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const hit = latin ? new RegExp(`(^|[^A-Za-z0-9])${esc}($|[^A-Za-z0-9])`, 'i').test(all) : all.includes(t);
    if (hit) out.push(`Banned word: "${t}"`);
  }
  if (kit.compliance.no_prices && PRICE.test(all)) out.push('Prices, offers or discounts are not allowed for this clinic');
  // a disclaimer box on any design must say something (a before and after has none: its wording
  // lives in the ad campaign, owner 2026-10-23)
  if (opts?.template) {
    for (const e of opts.template.elements)
      if (e.role === 'disclaimer' && !e.hidden && !(opts.texts?.[e.id] ?? '').trim()) {
        out.push('The disclaimer box is empty: add the disclaimer in Brand kit > Compliance'); break;
      }
  }
  return out;
}
/** Not blocking: what still has to happen outside this app before the static runs as an ad. */
export function complianceReminders(kit: BrandKit, t?: Template): string[] {
  if (!isBeforeAfter(t)) return [];
  const reg = kit.compliance.regulator || 'DHA, DOH or MOHAP';
  return [`Before it runs as an ad, the ${reg} advertising approval is needed${kit.compliance.ad_permit ? '' : ' (no ad permit number in the kit yet)'}`];
}

// ------------------------------------------------------------------ renderer
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Zoom (1 = cover) and offset (-1..1 of the overflow) for one photo, so two faces line up.
 *  Uniform scale only, so a photo is never stretched; zoom 1 fills the frame with no gap. */
export interface Crop { zoom: number; x: number; y: number }
export const MAX_ZOOM = 5;
/** smallest zoom: 1 fills the frame; a shrink frame may go to 0.4 so a portrait sits centred with space around it */
export const minZoom = (el: Pick<Element, 'shrink'>) => (el.shrink ? 0.4 : 1);
/** Where a photo lands in its frame: drawn size (dw, dh) and the shift (ox, oy) off centre.
 *  The one formula for the preview, the in-frame drag and the export (WYSIWYG). */
export function photoBox(el: Pick<Element, 'w' | 'h' | 'fit' | 'shrink'>, iw: number, ih: number, crop?: Crop) {
  const { w, h } = el;
  const ir = iw / ih, br = w / h;
  let dw = w, dh = h;
  if (el.fit === 'cover' ? ir > br : ir < br) dw = h * ir; else dh = w / ir;
  let ox = 0, oy = 0;
  if (crop && el.fit === 'cover') {
    const z = Math.max(minZoom(el), Math.min(MAX_ZOOM, crop.zoom || 1));
    dw *= z; dh *= z;
    ox = Math.max(-1, Math.min(1, crop.x || 0)) * (dw - w) / 2;
    oy = Math.max(-1, Math.min(1, crop.y || 0)) * (dh - h) / 2;
  }
  return { dw, dh, ox, oy };
}
function drawImage(ctx: CanvasRenderingContext2D, img: HTMLImageElement, el: Element, crop?: Crop, edge?: string) {
  const { x, y, w, h } = el;
  const f = [el.img_blur ? `blur(${el.img_blur}px)` : '', el.img_bw ? 'grayscale(1)' : '',
    el.img_brightness && el.img_brightness !== 1 ? `brightness(${el.img_brightness})` : ''].filter(Boolean).join(' ');
  // zoom keeps the shape (never stretched); the offset moves within what overflows the box
  const { dw, dh, ox, oy } = photoBox(el, img.naturalWidth, img.naturalHeight, crop);
  // the photo is the lower layer: clipped to the frame shape, the frame's stroke is drawn over it
  ctx.save();
  if (el.radius) { roundRect(ctx, x, y, w, h, el.radius); ctx.clip(); }
  else { ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip(); }
  if (f) ctx.filter = f;
  // a blurred image is drawn a little larger so its soft edge never shows inside the box
  const grow = el.img_blur ? el.img_blur * 2 : 0;
  const px = x + (w - dw) / 2 - ox, py = y + (h - dh) / 2 - oy;
  ctx.drawImage(img, px - grow, py - grow, dw + grow * 2, dh + grow * 2);
  // a photo set smaller than its frame (shrink): its edges fade into the colour around it, so a studio
  // backdrop's grey vignette never draws a visible box (Aziz 2026-10-26)
  if (edge && (dw < w - 1 || dh < h - 1)) {
    const f = Math.min(200, dw / 3, dh / 3), side = (gx0: number, gy0: number, gx1: number, gy1: number, rx: number, ry: number, rw: number, rh: number) => {
      const g = ctx.createLinearGradient(gx0, gy0, gx1, gy1); g.addColorStop(0, hexA(edge, 1)); g.addColorStop(1, hexA(edge, 0));
      ctx.fillStyle = g; ctx.fillRect(rx, ry, rw, rh); };
    if (dh < h - 1) { side(0, py, 0, py + f, px, py, dw, f); side(0, py + dh, 0, py + dh - f, px, py + dh - f, dw, f); }
    if (dw < w - 1) { side(px, 0, px + f, 0, px, py, f, dh); side(px + dw, 0, px + dw - f, 0, px + dw - f, py, f, dh); }
  }
  ctx.restore();
}

/** The fade that makes text readable on a photo: transparent to a kit colour at one edge. */
function drawScrim(ctx: CanvasRenderingContext2D, kit: BrandKit, el: Element) {
  if (!el.scrim || !el.scrim_strength) return;
  if (el.scrim_side === 'grid') { drawGridHaze(ctx, kit, el); return; }
  const side = el.scrim_side || 'bottom', k = el.scrim_size ?? 0.55;
  const { x, y, w, h } = el;
  const [x0, y0, x1, y1] = side === 'bottom' ? [x, y + h * (1 - k), x, y + h] : side === 'top' ? [x, y + h * k, x, y]
    : side === 'right' ? [x + w * (1 - k), y, x + w, y] : [x + w * k, y, x, y];
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  const c = colourOf(kit, el.scrim);
  g.addColorStop(0, hexA(c, 0)); g.addColorStop(1, hexA(c, el.scrim_strength));
  ctx.save(); roundRect(ctx, x, y, w, h, el.radius); ctx.clip(); ctx.fillStyle = g; ctx.fillRect(x, y, w, h); ctx.restore();
}

/** The grid haze, as on Vitch's live grid: a light veil over the whole photo, soft top and side edges,
 *  and a rise from the lower half that is nearly solid behind the bottom words, so clothing logos never
 *  show through. scrim_strength scales it (1 = the house look). */
function drawGridHaze(ctx: CanvasRenderingContext2D, kit: BrandKit, el: Element) {
  const { x, y, w, h } = el, c = colourOf(kit, el.scrim || 'white'), s = Math.max(0, Math.min(1.2, el.scrim_strength ?? 1));
  const a = (v: number) => hexA(c, Math.max(0, Math.min(1, v * s)));
  ctx.save(); roundRect(ctx, x, y, w, h, el.radius); ctx.clip();
  ctx.fillStyle = a(0.12); ctx.fillRect(x, y, w, h);
  let g = ctx.createLinearGradient(x, y, x, y + h * 0.22); g.addColorStop(0, a(0.38)); g.addColorStop(1, a(0));
  ctx.fillStyle = g; ctx.fillRect(x, y, w, h * 0.22);
  g = ctx.createLinearGradient(x, y, x + w, y);
  g.addColorStop(0, a(0.32)); g.addColorStop(0.16, a(0)); g.addColorStop(0.84, a(0)); g.addColorStop(1, a(0.32));
  ctx.fillStyle = g; ctx.fillRect(x, y, w, h);
  // the rise covers the bottom share set by scrim_size (default 0.55): nearly solid from just above the headline down
  const k = Math.max(0.2, Math.min(0.9, el.scrim_size ?? 0.55)), top = y + h * (1 - k);
  g = ctx.createLinearGradient(x, top, x, y + h);
  g.addColorStop(0, a(0)); g.addColorStop(0.3, a(0.85)); g.addColorStop(0.48, a(0.97)); g.addColorStop(0.65, a(1)); g.addColorStop(1, a(1));
  ctx.fillStyle = g; ctx.fillRect(x, top, w, h * k);
  ctx.restore();
}

/** WCAG contrast between two #rrggbb colours (1 to 21). */
export function contrast(a: string, b: string): number {
  const lum = (hex: string) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex); if (!m) return 0.5;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; })
      .reduce((s, c, i) => s + c * [0.2126, 0.7152, 0.0722][i], 0);
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

/** The colour actually behind a text box: the topmost colour block under its centre, else the
 *  template background. Null over a photo, where the designer's fade decides. */
function surfaceUnder(kit: BrandKit, t: Template, el: Element, hasBgImage: boolean): string | null {
  const cx = el.x + el.w / 2, cy = el.y + el.h / 2;
  const i = t.elements.indexOf(el);
  for (let k = i - 1; k >= 0; k--) {
    const o = t.elements[k];
    if (o.hidden || cx < o.x || cx > o.x + o.w || cy < o.y || cy > o.y + o.h) continue;
    if (IMAGE_ROLES.includes(o.role) && o.role !== 'logo') {
      // inside a strong fade the text sits on the fade colour, not on the photo
      if (o.scrim && (o.scrim_strength ?? 0) >= 0.7) {
        const k = o.scrim_size ?? 0.55, side = o.scrim_side || 'bottom';
        const inFade = side === 'grid' ? cy >= o.y + o.h * (1 - k * 0.7) : side === 'bottom' ? cy >= o.y + o.h * (1 - k) : side === 'top' ? cy <= o.y + o.h * k
          : side === 'right' ? cx >= o.x + o.w * (1 - k) : cx <= o.x + o.w * k;
        if (inFade) return colourOf(kit, o.scrim);
      }
      return null;
    }
    if (o.role === 'shape' && o.fill && (o.opacity ?? 1) > 0.6) return colourOf(kit, o.fill);
  }
  // a background photo faded most of the way out reads as the colour under it
  return hasBgImage && (t.bg_opacity ?? 1) > 0.3 ? null : colourOf(kit, t.background_colour || 'background');
}

/** Keep the designer's colour when it reads; otherwise the most readable kit colour. */
function readable(kit: BrandKit, want: string, surface: string | null, min: number): string {
  if (!surface || !/^#/.test(want) || contrast(want, surface) >= min) return want;
  const options = (['primary', 'text', 'white', 'secondary'] as ColourToken[]).map((k) => colourOf(kit, k));
  return options.reduce((best, c) => (contrast(c, surface) > contrast(best, surface) ? c : best), options[0]);
}

/** #rrggbb + alpha -> rgba() */
function hexA(hex: string, a: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function softShadow(ctx: CanvasRenderingContext2D, on: boolean, scale = 1) {
  if (!on) return;
  ctx.shadowColor = 'rgba(0,0,0,0.28)'; ctx.shadowBlur = 48 * scale; ctx.shadowOffsetY = 18 * scale;
}

let grainTile: HTMLCanvasElement | null = null;
/** Film grain, a fixed 256 px noise tile, so the same design always exports the same. */
function grain(): HTMLCanvasElement {
  if (grainTile) return grainTile;
  const c = document.createElement('canvas'); c.width = c.height = 256;
  const g = c.getContext('2d')!; const d = g.createImageData(256, 256);
  let seed = 7;
  for (let i = 0; i < d.data.length; i += 4) {
    seed = (seed * 16807) % 2147483647; const v = seed % 256;
    d.data[i] = d.data[i + 1] = d.data[i + 2] = v; d.data[i + 3] = 255;
  }
  g.putImageData(d, 0, 0);
  return (grainTile = c);
}

/** A colour wash over a photo (brand tint), clipped to the box. */
function drawTint(ctx: CanvasRenderingContext2D, kit: BrandKit, el: Element) {
  if (!el.tint || !el.tint_opacity) return;
  ctx.save();
  roundRect(ctx, el.x, el.y, el.w, el.h, el.radius); ctx.clip();
  ctx.globalAlpha = el.tint_opacity; ctx.fillStyle = colourOf(kit, el.tint);
  if (el.img_bw) ctx.globalCompositeOperation = 'multiply';
  ctx.fillRect(el.x, el.y, el.w, el.h);
  ctx.restore();
}

function drawStroke(ctx: CanvasRenderingContext2D, kit: BrandKit, el: Element) {
  if (!el.stroke || !el.stroke_w) return;
  ctx.save();
  ctx.strokeStyle = colourOf(kit, el.stroke); ctx.lineWidth = el.stroke_w;
  const i = el.stroke_w / 2;
  roundRect(ctx, el.x + i, el.y + i, el.w - el.stroke_w, el.h - el.stroke_w, Math.max(0, el.radius - i)); ctx.stroke();
  ctx.restore();
}

/** Flat fill, or a two-token gradient at the box's angle. */
function fillStyleFor(ctx: CanvasRenderingContext2D, kit: BrandKit, el: Element, token: ColourToken | '') {
  if (!el.gradient_to) return colourOf(kit, token);
  if (el.gradient_type === 'radial') {
    const cx = el.x + el.w / 2, cy = el.y + el.h / 2;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(el.w, el.h) * 0.72);
    g.addColorStop(0, colourOf(kit, token)); g.addColorStop(1, colourOf(kit, el.gradient_to));
    return g;
  }
  const a = ((el.gradient_angle ?? 90) - 90) * Math.PI / 180;
  const cx = el.x + el.w / 2, cy = el.y + el.h / 2;
  const r = Math.abs(el.w / 2 * Math.cos(a)) + Math.abs(el.h / 2 * Math.sin(a));
  const g = ctx.createLinearGradient(cx - Math.cos(a) * r, cy - Math.sin(a) * r, cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  g.addColorStop(0, colourOf(kit, token)); g.addColorStop(1, colourOf(kit, el.gradient_to));
  return g;
}

interface Tok { w: string; em: boolean; glue?: boolean }
/** "Glow *naturally*" -> words, with the starred ones marked for the emphasis colour. */
function tokens(text: string): Tok[][] {
  return text.split('\n').map((para) => {
    const out: Tok[] = []; let em = false; let prevEndsWord = false;
    for (const part of para.split('*')) {
      // no space on either side of the star = the same word continues ("*professionally*," keeps its comma)
      const glued = out.length > 0 && prevEndsWord && part.length > 0 && !/^\s/.test(part);
      part.split(/\s+/).filter(Boolean).forEach((w, i) => out.push({ w, em, glue: i === 0 && glued }));
      if (part.length) prevEndsWord = !/\s$/.test(part);
      em = !em;
    }
    return out;
  });
}

/** Draw a text box: shrinks until every line and every word fits its box. */
function drawText(ctx: CanvasRenderingContext2D, kit: BrandKit, el: Element, raw: string, lang: Lang, surface: string | null = null) {
  if (!raw.replace(/\*/g, '').trim()) return;
  const rtl = lang === 'ar';
  const text = el.upper && !rtl ? raw.toUpperCase() : raw;
  const paras = tokens(text);
  const family = familyFor(kit, slotFor(el, lang));
  const hl = el.highlight ? colourOf(kit, el.highlight) : '';
  const pill = el.role === 'cta' || (el.role === 'label' && !!el.fill);
  const pad = pill ? Math.round(el.h * 0.12) : 0;
  // Arabic letters reach far above and below the Latin line: never set it tighter than 1.3
  const lh = rtl ? Math.max(el.line_height || 1.5, 1.3) : (el.line_height || 1.22);
  const LS = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  // letter spacing breaks Arabic joining, so it applies to Latin only
  LS.letterSpacing = !rtl && el.tracking ? `${el.tracking}px` : '0px';
  const lineText = (l: Tok[]) => l.map((t, i) => (i && !t.glue ? ' ' : '') + t.w).join('');
  let size = rtl ? Math.round(el.size * 1.15) : el.size;
  const startSize = size;
  let lines: Tok[][] = [];
  for (; size >= Math.max(8, startSize * 0.35); size -= Math.max(1, Math.round(size * 0.04))) {
    ctx.font = `${el.weight} ${size}px "${family}", ${rtl ? '"Segoe UI", Tahoma' : '"Segoe UI", Arial'}, sans-serif`;
    const hp = hl ? size * 0.36 : 0;                     // a highlight bar needs room on both sides
    const boxW = el.w - pad * 2 - hp;
    lines = [];
    for (const para of paras) {
      let cur: Tok[] = [];
      for (const t of para) {
        const next = [...cur, t];
        if (!cur.length || ctx.measureText(lineText(next)).width <= boxW) cur = next;
        else { lines.push(cur); cur = [t]; }
      }
      lines.push(cur);
    }
    const widest = Math.max(...lines.map((l) => ctx.measureText(lineText(l)).width));
    const rowH = size * lh * (hl ? 1.08 : 1);
    if (lines.length <= el.max_lines && lines.length * rowH <= el.h + 0.5 && widest <= boxW + 0.5) break;
  }
  if (lines.length > el.max_lines) {
    lines = lines.slice(0, el.max_lines);
    const last = lines[lines.length - 1];
    if (last.length > 1) last.pop();
    last[last.length - 1] = { ...last[last.length - 1], w: last[last.length - 1].w + '…' };
  }
  if (pill && el.fill) {
    ctx.save(); ctx.globalAlpha = el.opacity; softShadow(ctx, el.shadow, 0.5);
    ctx.fillStyle = fillStyleFor(ctx, kit, el, el.fill);
    roundRect(ctx, el.x, el.y, el.w, el.h, el.radius); ctx.fill(); ctx.restore();
    drawStroke(ctx, kit, el);
  }
  const rowH = size * lh * (hl ? 1.08 : 1);
  const total = lines.length * rowH;
  const top = el.valign === 'middle' || pill ? el.y + (el.h - total) / 2 : el.valign === 'bottom' ? el.y + el.h - total : el.y;
  const side = el.align === 'center' ? 'center' : (el.align === 'start') !== rtl ? 'left' : 'right';
  const hp = hl ? size * 0.18 : 0;
  const space = ctx.measureText(' ').width;
  // a highlight bar is its own surface; otherwise whatever sits behind the box
  const behind = hl || (pill && el.fill ? colourOf(kit, el.fill) : surface);
  const base = el.colour ? readable(kit, colourOf(kit, el.colour), behind, 3) : 'transparent';
  // a marked word must stand out from its surface AND differ from the words around it
  let emc = readable(kit, colourOf(kit, el.emph_colour || 'accent'), behind, 2.2);
  if (contrast(emc, base) < 1.4 && behind) {
    const alt = (['accent', 'primary', 'secondary', 'white', 'text'] as ColourToken[]).map((k) => colourOf(kit, k))
      .filter((c) => contrast(c, behind) >= 2.2 && contrast(c, base) >= 1.4);
    if (alt.length) emc = alt[0];
  }
  const outline = !pill && el.stroke && el.stroke_w ? colourOf(kit, el.stroke) : '';
  ctx.save();
  // Opacity (fade) applies to text too (owner 2026-10-24); on a pill it fades the pill alone, as before
  if (!pill && (el.opacity ?? 1) < 1) ctx.globalAlpha = Math.max(0, el.opacity);
  // a line with no Arabic letters (a phone number and a website) keeps its own left-to-right order:
  // set right to left, "+971 50 ..." came out as "... 50 971+"
  ctx.direction = rtl && /[؀-ۿ]/.test(text) ? 'rtl' : 'ltr';
  ctx.textBaseline = 'middle';
  lines.forEach((line, i) => {
    const y = top + (i + 0.5) * rowH;
    const width = ctx.measureText(lineText(line)).width;
    // left edge of the line on the canvas, whatever the direction
    const left = side === 'center' ? el.x + (el.w - width) / 2 : side === 'left' ? el.x + pad + hp : el.x + el.w - pad - hp - width;
    const hasEm = line.some((t) => t.em);
    if (hl) {
      ctx.save(); ctx.fillStyle = hasEm ? emc : hl;
      roundRect(ctx, left - hp, y - size * 0.62, width + hp * 2, size * 1.24, size * 0.16); ctx.fill(); ctx.restore();
    }
    ctx.save();
    if (el.shadow && !hl) { ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = Math.round(size * 0.18); ctx.shadowOffsetY = Math.round(size * 0.04); }
    const plain = !hasEm;
    const fontOf = (em: boolean) => (em && el.emph_italic && !rtl ? `italic ${ctx.font.replace(/^italic /, '')}` : ctx.font.replace(/^italic /, ''));
    const paint = (s: string, x: number, align: CanvasTextAlign, colour: string) => {
      ctx.textAlign = align;
      if (outline) { ctx.lineJoin = 'round'; ctx.strokeStyle = outline; ctx.lineWidth = el.stroke_w; ctx.strokeText(s, x, y); }
      ctx.fillStyle = colour; ctx.fillText(s, x, y);
    };
    if (plain) {
      paint(lineText(line), rtl ? left + width : left, rtl ? 'right' : 'left', base);
    } else {
      // word by word in reading order: left to right in English, right to left in Arabic
      let x = rtl ? left + width : left;
      const normal = ctx.font;
      for (const [k, t] of line.entries()) {
        if (k && !t.glue) x += (rtl ? -1 : 1) * space;
        ctx.font = fontOf(t.em);
        const wW = ctx.measureText(t.w).width;
        // on a highlight pill the marked line's pill is the emphasis colour, so its words stay the base colour
        paint(t.w, x, rtl ? 'right' : 'left', t.em && !hl ? emc : (hl && hasEm ? colourOf(kit, 'white') : base));
        ctx.font = normal;
        x += (rtl ? -1 : 1) * wW;
      }
    }
    ctx.restore();
  });
  ctx.restore();
  LS.letterSpacing = '0px';
}

export interface RenderInput {
  kit: BrandKit; template: Template; lang: Lang;
  texts: Record<string, string>;                      // element id -> text
  images: Record<string, HTMLImageElement | null>;    // element id -> image
  background: HTMLImageElement | null;
  crops?: Record<string, Crop>;                       // element id -> zoom / offset (before and after)
}
/** A before and after whose benefit line is empty: the bar is hidden and the photos (and their
 *  frame) move down by half its band, so the pair re-centres (owner 2026-10-23). */
export function collapseEmptyBenefit(t: Template, texts: Record<string, string>): Template {
  if (!isBeforeAfter(t)) return t;
  const bar = t.elements.find((e) => e.role === 'cta' && !e.hidden);
  if (!bar || (texts[bar.id] ?? '').replace(/\*/g, '').trim()) return t;
  const photos = t.elements.filter((e) => BEFORE_AFTER_ROLES.includes(e.role));
  const touches = (e: Element) => photos.some((p) => e.x < p.x + p.w && p.x < e.x + e.w && e.y < p.y + p.h && p.y < e.y + e.h);
  const moving = t.elements.filter((e) => BEFORE_AFTER_ROLES.includes(e.role) || ((e.role === 'shape' || e.role === 'label') && touches(e)));
  const bottom = Math.max(...moving.map((e) => e.y + e.h));
  const shift = Math.max(0, Math.round((bar.y + bar.h - bottom) / 2));
  return { ...t, elements: t.elements.filter((e) => e !== bar).map((e) => (moving.includes(e) ? { ...e, y: e.y + shift } : e)) };
}

/** The boxes exactly where renderDesign draws them (Arabic mirror, empty benefit line). */
export function laidOut(t: Template, lang: Lang, texts: Record<string, string>): Template {
  return collapseEmptyBenefit(placed(t, lang), texts);
}

/** The background photo's zoom and offset, in the photo frame's terms. */
export function bgCrop(t: Template): Crop {
  return { zoom: t.bg_scale ?? 1, x: t.bg_offset_x ?? 0, y: t.bg_offset_y ?? 0 };
}

let fadeCanvas: HTMLCanvasElement | null = null;
/** A scratch layer the size of the design, cleared, for drawing one element before its fade mask. */
function scratch(w: number, h: number): CanvasRenderingContext2D {
  const c = fadeCanvas ?? (fadeCanvas = document.createElement('canvas'));
  if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
  const g = c.getContext('2d')!;
  g.setTransform(1, 0, 0, 1, 0, 0); g.globalAlpha = 1; g.globalCompositeOperation = 'source-over'; g.filter = 'none';
  g.clearRect(0, 0, w, h);
  return g;
}
/** Keep what is drawn on `g`, fading from `a0` (visible at the start) to `a1` across the box: linear
 *  toward `side`, or radial from the centre out. The gradient pads past the box with its end values. */
function applyFade(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
  type: 'linear' | 'radial', side: FadeSide, a0: number, a1: number) {
  let grad: CanvasGradient;
  if (type === 'radial') {
    const cx = x + w / 2, cy = y + h / 2;
    grad = g.createRadialGradient(cx, cy, 0, cx, cy, Math.hypot(w, h) / 2);
  } else {
    const to: Record<FadeSide, [number, number, number, number]> = {
      bottom: [x, y, x, y + h], top: [x, y + h, x, y], right: [x, y, x + w, y], left: [x + w, y, x, y],
      bottom_right: [x, y, x + w, y + h], bottom_left: [x + w, y, x, y + h], top_right: [x, y + h, x + w, y], top_left: [x + w, y + h, x, y],
    };
    const [x0, y0, x1, y1] = to[side] ?? to.bottom;
    grad = g.createLinearGradient(x0, y0, x1, y1);
  }
  grad.addColorStop(0, `rgba(0,0,0,${Math.max(0, Math.min(1, a0))})`);
  grad.addColorStop(1, `rgba(0,0,0,${Math.max(0, Math.min(1, a1))})`);
  g.save(); g.globalCompositeOperation = 'destination-in'; g.fillStyle = grad;
  // the mask follows the box (rotation included): the caller's transform is still on g
  g.fillRect(-g.canvas.width * 2, -g.canvas.height * 2, g.canvas.width * 5, g.canvas.height * 5);
  g.restore();
}
const isFaded = (el: Element) => !!el.fade && ((el.fade_start ?? 1) < 1 || (el.fade_end ?? 0) < 1);

/** The text an icon element sets as a glyph in the brand font, or '' for a line icon. */
export function iconGlyph(el: Element, lang: Lang): string {
  const def = ICONS.find((i) => i.key === (el.icon || 'question'));
  if (def?.glyph) return lang === 'ar' ? def.glyph.ar : def.glyph.en;
  if (el.icon === 'text') return ((lang === 'ar' ? el.default_ar : el.default_en) || el.default_en || el.default_ar || '').trim();
  return '';
}
function drawIcon(ctx: CanvasRenderingContext2D, kit: BrandKit, el: Element, lang: Lang) {
  const glyph = iconGlyph(el, lang);
  ctx.save();
  ctx.globalAlpha = Math.max(0, el.opacity ?? 1);
  softShadow(ctx, el.shadow, 0.5);
  if (glyph) {
    // a symbol in the clinic's own font, as large as the box allows
    const family = familyFor(kit, slotFor(el, lang));
    let size = el.h;
    ctx.font = `${el.weight} ${size}px "${family}", "Segoe UI", sans-serif`;
    const w0 = ctx.measureText(glyph).width;
    if (w0 > el.w) { size = Math.max(6, Math.floor(size * el.w / w0)); ctx.font = `${el.weight} ${size}px "${family}", "Segoe UI", sans-serif`; }
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    ctx.direction = lang === 'ar' && /[؀-ۿ]/.test(glyph) ? 'rtl' : 'ltr';
    // centre the ink, not the line box: a question mark sits high on its line otherwise
    const m = ctx.measureText(glyph);
    const ink = (m.actualBoundingBoxAscent || size * 0.7) - (m.actualBoundingBoxDescent || 0);
    ctx.fillStyle = colourOf(kit, el.colour || 'text');
    ctx.fillText(glyph, el.x + el.w / 2, el.y + el.h / 2 + ink / 2);
  } else {
    const def = ICONS.find((i) => i.key === el.icon) ?? ICONS.find((i) => i.key === 'star')!;
    const s = Math.min(el.w, el.h) / 24;
    ctx.translate(el.x + (el.w - 24 * s) / 2, el.y + (el.h - 24 * s) / 2); ctx.scale(s, s);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(0.4, (el.weight || 600) / 300);
    for (const d of def.paths ?? []) {
      const p = new Path2D(d);
      if (el.fill) { ctx.fillStyle = colourOf(kit, el.fill); ctx.fill(p); }
      if (el.colour) { ctx.strokeStyle = colourOf(kit, el.colour); ctx.stroke(p); }
    }
  }
  ctx.restore();
}

export function renderDesign(canvas: HTMLCanvasElement, r: RenderInput) {
  const { kit } = r;
  const t = laidOut(r.template, r.lang, r.texts);
  canvas.width = t.w; canvas.height = t.h;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, t.w, t.h);
  const full = { x: 0, y: 0, w: t.w, h: t.h, radius: 0, gradient_to: t.bg_gradient_to, gradient_type: t.bg_gradient_type,
    gradient_angle: t.bg_gradient_angle ?? 180 } as Element;
  ctx.fillStyle = fillStyleFor(ctx, kit, full, t.background_colour || 'background');
  ctx.fillRect(0, 0, t.w, t.h);
  if (r.background) {
    // the bottom layer: faded toward the colour under it, zoomed and moved like a photo frame,
    // and optionally faded out gradually (a mask) on a scratch layer first
    const fade = Math.max(0, Math.min(1, t.bg_opacity ?? 1));
    const masked = !!t.bg_fade && ((t.bg_fade_start ?? 1) < 1 || (t.bg_fade_end ?? 0) < 1);
    const g = masked ? scratch(t.w, t.h) : ctx;
    g.save(); g.globalAlpha = fade;
    drawImage(g, r.background, { ...full, fit: 'cover', img_blur: t.bg_blur } as Element, bgCrop(t));
    g.restore();
    if (t.bg_tint && t.bg_tint_opacity) { g.save(); g.globalAlpha = t.bg_tint_opacity * fade; g.fillStyle = colourOf(kit, t.bg_tint); g.fillRect(0, 0, t.w, t.h); g.restore(); }
    if (masked) {
      applyFade(g, 0, 0, t.w, t.h, t.bg_fade as 'linear' | 'radial', t.bg_fade_side ?? 'bottom', t.bg_fade_start ?? 1, t.bg_fade_end ?? 0);
      ctx.drawImage(g.canvas, 0, 0);
    }
  }
  if (t.bg_grain) {
    ctx.save(); ctx.globalAlpha = Math.min(0.5, t.bg_grain * 0.35); ctx.globalCompositeOperation = 'overlay';
    ctx.fillStyle = ctx.createPattern(grain(), 'repeat')!; ctx.fillRect(0, 0, t.w, t.h); ctx.restore();
  }
  for (const el of t.elements) {
    if (el.hidden) continue;
    const rot = el.rotation && el.role !== 'logo' && (el.role === 'shape' || el.role === 'icon' || IMAGE_ROLES.includes(el.role)) ? el.rotation : 0;
    // a faded or blended layer is drawn alone on a scratch layer, masked, then laid on the design
    // with its blend mode, so it never changes how any other layer draws
    const g = isFaded(el) || el.blend ? scratch(t.w, t.h) : ctx;
    g.save();
    if (rot) { g.translate(el.x + el.w / 2, el.y + el.h / 2); g.rotate(rot * Math.PI / 180); g.translate(-(el.x + el.w / 2), -(el.y + el.h / 2)); }
    drawElement(g, r, kit, el, t);
    if (g !== ctx && isFaded(el)) applyFade(g, el.x, el.y, el.w, el.h, el.fade as 'linear' | 'radial', el.fade_side ?? 'bottom', el.fade_start ?? 1, el.fade_end ?? 0);
    g.restore();
    if (g !== ctx) {
      ctx.save(); ctx.globalCompositeOperation = (el.blend || 'source-over') as GlobalCompositeOperation;
      ctx.drawImage(g.canvas, 0, 0); ctx.restore();
    }
  }
}

function drawElement(ctx: CanvasRenderingContext2D, r: RenderInput, kit: BrandKit, el: Element, t: Template) {
  {
    if (el.role === 'icon') {
      drawIcon(ctx, kit, el, r.lang);
    } else if (el.role === 'shape') {
      ctx.save(); ctx.globalAlpha = el.opacity; softShadow(ctx, el.shadow);
      ctx.fillStyle = el.fill || el.gradient_to ? fillStyleFor(ctx, kit, el, el.fill || el.colour) : 'transparent';
      roundRect(ctx, el.x, el.y, el.w, el.h, el.radius); ctx.fill(); ctx.restore();
      drawStroke(ctx, kit, el);
    } else if (IMAGE_ROLES.includes(el.role)) {
      const img = r.images[el.id];
      if (!img && BEFORE_AFTER_ROLES.includes(el.role)) {   // an empty card still shows where the photo goes
        ctx.save(); ctx.globalAlpha = 0.14; ctx.fillStyle = colourOf(kit, 'secondary');
        roundRect(ctx, el.x, el.y, el.w, el.h, el.radius); ctx.fill(); ctx.restore();
      }
      if (img) {
        if (el.shadow && el.role !== 'logo') {   // the shadow is cast by a solid card under the photo
          ctx.save(); softShadow(ctx, true); ctx.fillStyle = colourOf(kit, 'background');
          roundRect(ctx, el.x, el.y, el.w, el.h, el.radius); ctx.fill(); ctx.restore();
        }
        ctx.save(); ctx.globalAlpha = el.opacity; drawImage(ctx, img, el, r.crops?.[el.id], el.shrink ? colourOf(kit, t.background_colour || 'background') : undefined); ctx.restore();
        if (el.role !== 'logo') { drawTint(ctx, kit, el); drawScrim(ctx, kit, el); }
      }
      drawStroke(ctx, kit, el);
    } else {
      drawText(ctx, kit, el, r.texts[el.id] ?? '', r.lang, surfaceUnder(kit, t, el, !!r.background));
    }
  }
}

export function canvasBlob(canvas: HTMLCanvasElement, type = 'image/png'): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('export failed'))), type, 0.92));
}

// ------------------------------------------------------------------ defaults
let seq = 0;
export function newElement(role: Role, t: Template): Element {
  const big = ['headline', 'name'].includes(role);
  const img = IMAGE_ROLES.includes(role);
  const n = t.elements.length;     // cascade, so new boxes never sit on top of each other
  if (role === 'icon') {
    const side = Math.round(Math.min(t.w, t.h) * 0.45);
    return { ...newElement('shape', t), role, x: Math.round(t.w - side - t.w * 0.06), y: Math.round(t.h * 0.08), w: side, h: side,
      fill: '', colour: 'accent', font: 'en_primary', weight: 600, opacity: 0.25, icon: 'question', radius: 0 };
  }
  return {
    id: `n${Date.now().toString(36)}${seq++}`, role,
    x: Math.round(t.w * (0.06 + (n % 3) * 0.04)), y: Math.round(t.h * (0.06 + (n % 8) * 0.1)),
    w: Math.round(role === 'logo' ? t.w * 0.3 : role === 'cta' ? t.w * 0.5 : role === 'label' ? t.w * 0.2 : t.w * 0.84),
    h: Math.round(img ? (role === 'logo' ? t.w * 0.12 : t.h * 0.45) : big ? t.h * 0.12 : role === 'cta' || role === 'label' ? t.h * 0.05 : t.h * 0.08),
    font: big || role === 'subheadline' ? 'en_primary' : 'en_secondary',
    size: big ? 72 : role === 'subheadline' ? 44 : role === 'cta' || role === 'label' ? 30 : ['caption', 'disclaimer', 'contact'].includes(role) ? 22 : 30,
    weight: big ? 700 : role === 'cta' ? 600 : 400,
    colour: role === 'cta' || role === 'label' ? 'white' : 'text', fill: role === 'cta' || role === 'label' || role === 'shape' ? 'primary' : '',
    align: role === 'label' ? 'center' : 'start', valign: role === 'cta' || role === 'label' ? 'middle' : 'top', max_lines: big ? 2 : role === 'cta' || role === 'label' ? 1 : 4,
    radius: role === 'cta' || role === 'label' ? 999 : ['photo', 'before_photo', 'after_photo'].includes(role) ? 24 : 0, fit: role === 'logo' ? 'contain' : 'cover', opacity: 1,
    default_en: role === 'cta' ? 'Book on WhatsApp' : role === 'label' ? 'Before' : '', default_ar: role === 'cta' ? 'احجز عبر واتساب' : role === 'label' ? 'قبل' : '', locked: false, asset: '',
    line_height: 0, tracking: 0, upper: false, shadow: false, gradient_to: '', gradient_angle: 90, stroke: '', stroke_w: 0,
    tint: '', tint_opacity: 0, hidden: false, pinned: false,
  };
}
/** Add a new box where a designer expects it: photos and colour blocks just above the
 *  background, text and logos in front (owner 2026-10-24). Returns the new list. */
export function withNewElement(elements: Element[], el: Element): Element[] {
  return BACK_ROLES.includes(el.role) ? [el, ...elements] : [...elements, el];
}
/** Move one layer: to the front or the back, one step up or down, or to an index of the list
 *  without it (0 = back). The list draws back to front. */
export function moveLayer(elements: Element[], id: string, to: 'front' | 'back' | 'up' | 'down' | number): Element[] {
  const a = [...elements];
  const i = a.findIndex((x) => x.id === id);
  if (i < 0) return elements;
  const [x] = a.splice(i, 1);
  const j = to === 'front' ? a.length : to === 'back' ? 0 : to === 'up' ? Math.min(a.length, i + 1) : to === 'down' ? Math.max(0, i - 1) : Math.max(0, Math.min(a.length, to));
  a.splice(j, 0, x);
  return a;
}

export function newTemplate(fmt = FORMATS[0], name = 'New template'): Template {
  return { id: '', name, purpose: 'custom', lang: 'both', w: fmt.w, h: fmt.h, background: '', background_colour: 'background', mirror_ar: true, elements: [] };
}
export function emptyPerson(name = ''): Person {
  return { name_en: name, name_ar: '', title_en: '', title_ar: '', licence: '', specialties_en: '', specialties_ar: '', languages: '',
    days_en: '', days_ar: '', bio_en: '', bio_ar: '', photo: '', ads_consent: false, shown: true };
}


// ------------------------------------------------------------------ designer helpers
/** The same design on another format: boxes scale with the canvas, type with the
 *  smaller ratio so nothing overflows. A start point the designer then polishes. */
export function adaptTemplate(t: Template, w: number, h: number, name: string): Template {
  const sx = w / t.w, sy = h / t.h, s = Math.min(sx, sy);
  return {
    ...structuredClone(t), id: '', name, w, h,
    elements: t.elements.map((e) => {
      const img = IMAGE_ROLES.includes(e.role) && e.role !== 'logo';
      // photos keep their shape: scale by the smaller ratio and stay centred on their old centre
      const nw = Math.round(img ? e.w * s : e.w * sx), nh = Math.round(img ? e.h * s : e.h * sy);
      const cx = (e.x + e.w / 2) * sx, cy = (e.y + e.h / 2) * sy;
      return { ...e, x: Math.round(cx - nw / 2), y: Math.round(cy - nh / 2), w: nw, h: nh,
        size: Math.round(e.size * s), radius: Math.round(e.radius * s), stroke_w: Math.round(e.stroke_w * s) };
    }),
  };
}

export interface Guide { axis: 'x' | 'y'; at: number }
/** Snap a moving box to the canvas edges, centre and margins and to the other boxes.
 *  Returns the snapped position and the guide lines to draw. `tol` is in canvas px. */
export function snapBox(t: Template, id: string, x: number, y: number, w: number, h: number, margin: number, tol: number) {
  // the canvas (edges, margins, centre) wins over other boxes: that is what a designer expects
  const canvasX = [0, margin, t.w / 2, t.w - margin, t.w];
  const canvasY = [0, margin, t.h / 2, t.h - margin, t.h];
  const boxX: number[] = [], boxY: number[] = [];
  for (const o of t.elements) {
    if (o.id === id || o.hidden) continue;
    boxX.push(o.x, o.x + o.w / 2, o.x + o.w); boxY.push(o.y, o.y + o.h / 2, o.y + o.h);
  }
  const guides: Guide[] = [];
  const best = (edges: number[], targets: number[]) => {
    let d = Infinity, at = 0;
    for (const e of edges) for (const tg of targets) { const k = tg - e; if (Math.abs(k) < Math.abs(d)) { d = k; at = tg; } }
    return Math.abs(d) <= tol ? { shift: d, at } : null;
  };
  const ex = [x, x + w / 2, x + w], ey = [y, y + h / 2, y + h];
  const bx = best(ex, canvasX) ?? best(ex, boxX);
  const by = best(ey, canvasY) ?? best(ey, boxY);
  if (bx) { x += bx.shift; guides.push({ axis: 'x', at: bx.at }); }
  if (by) { y += by.shift; guides.push({ axis: 'y', at: by.at }); }
  return { x: Math.round(x), y: Math.round(y), guides };
}


// ------------------------------------------------------------------ recipes
export interface Recipe { key: string; name: string; use: string; template: Template }
export async function fetchRecipes(clientId: string, w: number, h: number): Promise<Recipe[]> {
  return (await api.get(`/clients/${clientId}/brand-kit/recipes`, { params: { w, h } })).data;
}
